import {
  type ComputedRef,
  computed,
  onBeforeUnmount,
  onMounted,
  proxyRefs,
  type Ref,
  ref,
  type ShallowRef,
  shallowRef,
  type WatchStopHandle,
  watch,
} from 'vue';
import { currentLocale } from '@/app/i18n';
import { router } from '@/app/router';
import { store } from '@/app/store';
import { useUiStore } from '@/app/stores/ui';
import { restApi } from '@/services/api';
import { idleWatcher } from '@/services/IdleWatcher';
import { printing } from '@/services/Printing';
import { session } from '@/services/Session';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
import {
  areHohPortraitsStillLoading,
  expandPortraitCopies,
  type GoPhotoUploadPortrait,
  isHohAllPortraitsKiosk,
  retryDelayWithJitter,
  selectGoPhotoPortraits,
} from './gophotoUploadSelection';
import {
  trackAutoRetry,
  trackBackgroundAccepted,
  trackExhaustedException,
  trackExhaustedFailures,
  trackKioskReset,
  trackManualFullRetry,
  trackManualPartialRetry,
  trackPartialAccepted,
  trackTransferCompleted,
  trackTransferStarted,
  trackUploadBatch,
  trackUploadExceptionAttempt,
  trackUploadStarted,
  trackUploadSuccess,
  trackValidationFailure,
  withTransferSpan,
  withUploadProcessSpan,
} from './gophotoUploadTelemetry';

export type GoPhotoUploadViewState =
  | 'idle'
  | 'waiting'
  | 'uploading'
  | 'partial'
  | 'success'
  | 'error';

interface IdleWatcherPause {
  continue: () => void;
}

interface UseGoPhotoUploadFlowOptions {
  translate: (key: string, fallback: string) => string;
}

interface UseGoPhotoUploadFlowResult {
  errorMessage: ShallowRef<string>;
  failedUuids: Ref<string[]>;
  finishWithPartial: () => void;
  gophotoCode: ComputedRef<string>;
  publicApi: GoPhotoUploadPublicApi;
  retry: () => void;
  retryFailed: () => void;
  showGophotoCode: ComputedRef<boolean>;
  successCount: ShallowRef<number>;
  totalCount: ShallowRef<number>;
  uploadedCount: ShallowRef<number>;
  viewState: ComputedRef<GoPhotoUploadViewState>;
}

export interface GoPhotoUploadPublicApi {
  AUTO_RESET_DELAY_BACKGROUND: number;
  AUTO_RESET_DELAY_SYNC: number;
  AUTO_RETRY_DELAYS: number[];
  MAX_AUTO_RETRIES: number;
  USE_BACKGROUND_UPLOAD: boolean;
  _doStartUpload: (uuidsToUpload: string[] | null) => Promise<void>;
  _isMounted: boolean;
  _isUploadInFlight: boolean;
  _retryDelayWithJitter: (attempt: number) => number;
  autoResetTimeout: ReturnType<typeof setTimeout> | null;
  autoRetryAttempt: number;
  errorMessage: string;
  failedUuids: string[];
  finishWithPartial: () => void;
  readonly gophotoCode: string;
  hasError: boolean;
  readonly hohPortraitsStillLoading: boolean;
  readonly hohSendsAllPortraits: boolean;
  idleWatcherPause: IdleWatcherPause | null;
  isPartialSuccess: boolean;
  isSuccess: boolean;
  isUploading: boolean;
  resetKiosk: () => Promise<void>;
  retry: () => void;
  retryCount: number;
  retryFailed: () => void;
  readonly selectedPortraits: GoPhotoUploadPortrait[];
  readonly showGophotoCode: boolean;
  startUpload: (uuidsToUpload?: string[] | null) => Promise<void>;
  readonly store: typeof store;
  successCount: number;
  totalCount: number;
  uploadedCount: number;
  uploadStartTime: number | null;
  readonly viewState: GoPhotoUploadViewState;
}

export function useGoPhotoUploadFlow({
  translate,
}: UseGoPhotoUploadFlowOptions): UseGoPhotoUploadFlowResult {
  const isUploading = shallowRef(false);
  const isSuccess = shallowRef(false);
  const isPartialSuccess = shallowRef(false);
  const hasError = shallowRef(false);
  const errorMessage = shallowRef('');
  const uploadedCount = shallowRef(0);
  const totalCount = shallowRef(0);
  const successCount = shallowRef(0);
  const failedUuids = ref<string[]>([]);
  const useBackgroundUpload = shallowRef(true);
  const autoResetDelayBackground = shallowRef(5000);
  const autoResetDelaySync = shallowRef(15_000);
  const maxAutoRetries = shallowRef(3);
  const autoRetryDelays = shallowRef([2000, 4000, 8000]);
  const autoRetryAttempt = shallowRef(0);
  const retryCount = shallowRef(0);
  const uploadStartTime = shallowRef<number | null>(null);
  const isMounted = shallowRef(true);
  const isUploadInFlight = shallowRef(false);

  let idleWatcherPause: IdleWatcherPause | null = null;
  let autoResetTimeout: ReturnType<typeof setTimeout> | null = null;
  let stopHohWatch: WatchStopHandle | null = null;

  const gophotoCode = computed(() => session.gophotoCode || '');
  const hohSendsAllPortraits = computed(() =>
    isHohAllPortraitsKiosk(store.kiosk?.slug)
  );
  const showGophotoCode = computed(() => !hohSendsAllPortraits.value);
  const selectedPortraits = computed(() =>
    selectGoPhotoPortraits({
      hohSendsAllPortraits: hohSendsAllPortraits.value,
      portraits: (store.portraits || []) as GoPhotoUploadPortrait[],
      printingQueue: (printing.queue || []) as GoPhotoUploadPortrait[],
    })
  );
  const hohPortraitsStillLoading = computed(() =>
    session.gophotoSendReadyOnly
      ? false
      : areHohPortraitsStillLoading(
          hohSendsAllPortraits.value,
          (store.portraits || []) as GoPhotoUploadPortrait[]
        )
  );
  const viewState = computed<GoPhotoUploadViewState>(() => {
    if (isUploading.value) {
      return 'uploading';
    }
    if (isPartialSuccess.value) {
      return 'partial';
    }
    if (isSuccess.value) {
      return 'success';
    }
    if (hasError.value) {
      return 'error';
    }
    if (hohPortraitsStillLoading.value) {
      return 'waiting';
    }
    return 'idle';
  });

  function scheduleReset(delay: number): void {
    if (autoResetTimeout) {
      clearTimeout(autoResetTimeout);
    }
    autoResetTimeout = setTimeout(() => {
      if (isMounted.value) {
        void resetKiosk();
      }
    }, delay);
  }

  function configuredRetryDelay(attempt: number): number {
    return retryDelayWithJitter(attempt, autoRetryDelays.value);
  }

  function clearAutoReset(): void {
    if (autoResetTimeout) {
      clearTimeout(autoResetTimeout);
      autoResetTimeout = null;
    }
  }

  function failValidation(
    key: string,
    fallback: string,
    telemetryMessage: string
  ): void {
    hasError.value = true;
    errorMessage.value = translate(key, fallback);
    trackValidationFailure(telemetryMessage);
  }

  // takeRetryUuids is a function, not a value: the results branch reads and
  // clears failedUuids only after the delay and only if the component is mounted.
  async function autoRetry(
    takeRetryUuids: () => string[] | null,
    warnText: (delay: number) => string,
    onRetryScheduled?: (delay: number) => void
  ): Promise<void> {
    autoRetryAttempt.value += 1;
    const delay = configuredRetryDelay(autoRetryAttempt.value);
    onRetryScheduled?.(delay);

    // biome-ignore lint/suspicious/noConsole: preserve operational retry diagnostics.
    console.warn(warnText(delay));

    isUploading.value = true;
    await new Promise((resolve) => setTimeout(resolve, delay));

    if (!isMounted.value) {
      return;
    }

    const retryUuids = takeRetryUuids();
    isUploadInFlight.value = false;
    await startUpload(retryUuids);
  }

  async function startUpload(
    uuidsToUpload: string[] | null = null
  ): Promise<void> {
    if (isUploadInFlight.value) {
      // biome-ignore lint/suspicious/noConsole: preserve operational diagnostics for duplicate kiosk input.
      console.warn('[GoPhotoUpload] startUpload() already in flight, ignoring');
      return;
    }
    isUploadInFlight.value = true;

    try {
      await doStartUpload(uuidsToUpload);
    } finally {
      isUploadInFlight.value = false;
    }
  }

  async function doStartUpload(uuidsToUpload: string[] | null): Promise<void> {
    const isRetry = uuidsToUpload !== null;

    if (isRetry) {
      retryCount.value += 1;
    } else {
      retryCount.value = 0;
    }

    trackUploadStarted({
      isRetry,
      retryCount: retryCount.value,
      gophotoCode: gophotoCode.value,
    });

    const imageUuids = uuidsToUpload
      ? uuidsToUpload
      : expandPortraitCopies(selectedPortraits.value);

    if (imageUuids.length === 0) {
      failValidation(
        'GoPhotoUpload.noImages',
        'No images selected for upload.',
        'Upload failed - no images selected'
      );
      return;
    }

    if (!gophotoCode.value) {
      failValidation(
        'GoPhotoUpload.noCode',
        'PhotoPass code not found. Please start over.',
        'Upload failed - no PhotoPass code'
      );
      return;
    }

    if (!session.id) {
      failValidation(
        'GoPhotoUpload.sessionExpired',
        'Session expired. Please start over.',
        'Upload failed - session expired before upload started'
      );
      return;
    }

    isUploading.value = true;
    isPartialSuccess.value = false;
    hasError.value = false;
    totalCount.value = uuidsToUpload ? totalCount.value : imageUuids.length;
    uploadedCount.value = uuidsToUpload ? successCount.value : 0;
    uploadStartTime.value = Date.now();

    trackUploadBatch({ imageUuids, isRetry });

    const gophotoCodeSnapshot = gophotoCode.value;

    try {
      await withUploadProcessSpan(
        { imageUuids, isRetry, retryCount: retryCount.value },
        async () => {
          trackTransferStarted(imageUuids.length);

          const transferStartTime = Date.now();
          const result = await withTransferSpan(imageUuids.length, () =>
            restApi.uploadToGophoto(imageUuids, gophotoCodeSnapshot, {
              background: useBackgroundUpload.value,
            })
          );
          const transferDuration = Date.now() - transferStartTime;
          trackTransferCompleted(result, imageUuids.length, transferDuration);

          if (!isMounted.value) {
            return;
          }

          if (result?.accepted === true) {
            successCount.value = totalCount.value;
            uploadedCount.value = totalCount.value;
            failedUuids.value = [];
            trackBackgroundAccepted(imageUuids.length, transferDuration);
            isUploading.value = false;
            isSuccess.value = true;
            scheduleReset(autoResetDelayBackground.value);
            return;
          }

          const failedResults =
            result.results?.filter((item) => !item.success) || [];
          failedUuids.value = failedResults.map((item) => item.image_uuid);

          const newSuccesses = result.successful_uploads || 0;
          successCount.value =
            (uuidsToUpload ? successCount.value : 0) + newSuccesses;
          uploadedCount.value = successCount.value;

          const totalDuration = Date.now() - Number(uploadStartTime.value);

          if (failedUuids.value.length > 0) {
            const allFailed =
              result.successful_uploads === 0 && successCount.value === 0;

            if (autoRetryAttempt.value < maxAutoRetries.value) {
              await autoRetry(
                () => {
                  const retryUuids = [...failedUuids.value];
                  failedUuids.value = [];
                  return retryUuids;
                },
                (delay) =>
                  `[GoPhotoUpload] Auto-retry ${autoRetryAttempt.value}/${maxAutoRetries.value} ` +
                  `for ${failedUuids.value.length} failed uploads in ${delay}ms`,
                (delay) =>
                  trackAutoRetry({
                    allFailed,
                    attempt: autoRetryAttempt.value,
                    delay,
                    failedUuids: failedUuids.value,
                    maxRetries: maxAutoRetries.value,
                  })
              );
              return;
            }

            trackExhaustedFailures({
              allFailed,
              attempt: autoRetryAttempt.value,
              failedResults,
              failedUuids: failedUuids.value,
              gophotoCode: gophotoCodeSnapshot,
              retryCount: retryCount.value,
              successCount: successCount.value,
              totalCount: totalCount.value,
            });

            isUploading.value = false;
            if (allFailed) {
              hasError.value = true;
              errorMessage.value = translate(
                'GoPhotoUpload.allFailed',
                'Failed to upload photos. Please try again.'
              );
            } else {
              isPartialSuccess.value = true;
            }
            return;
          }

          isUploading.value = false;
          trackUploadSuccess({
            duration: totalDuration,
            retryCount: retryCount.value,
            totalCount: totalCount.value,
          });
          isSuccess.value = true;
          scheduleReset(autoResetDelaySync.value);
        }
      );
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: preserve operational upload diagnostics.
      console.error('[GoPhotoUpload] Upload failed:', error);

      const totalDuration = Date.now() - Number(uploadStartTime.value);
      trackUploadExceptionAttempt({
        attempt: autoRetryAttempt.value,
        duration: totalDuration,
        error,
      });

      if (!isMounted.value) {
        return;
      }

      if (autoRetryAttempt.value < maxAutoRetries.value) {
        await autoRetry(
          () => uuidsToUpload || null,
          (delay) =>
            `[GoPhotoUpload] Exception auto-retry ${autoRetryAttempt.value}/${maxAutoRetries.value} in ${delay}ms`
        );
        return;
      }

      trackExhaustedException({
        attempt: autoRetryAttempt.value,
        duration: totalDuration,
        error,
        gophotoCode: gophotoCodeSnapshot,
        imageUuids,
        retryCount: retryCount.value,
      });

      isUploading.value = false;
      hasError.value = true;
      errorMessage.value = translate(
        'GoPhotoUpload.uploadFailed',
        'Failed to upload photos. Please try again.'
      );
    } finally {
      if (isMounted.value && isUploading.value && !isUploadInFlight.value) {
        // biome-ignore lint/suspicious/noConsole: preserve stuck-upload recovery diagnostics.
        console.warn(
          '[GoPhotoUpload] isUploading still true in finally, forcing reset'
        );
        isUploading.value = false;
      }
    }
  }

  function retry(): void {
    if (isUploadInFlight.value) {
      return;
    }

    clearAutoReset();

    trackManualFullRetry(retryCount.value);
    hasError.value = false;
    errorMessage.value = '';
    failedUuids.value = [];
    successCount.value = 0;
    autoRetryAttempt.value = 0;
    void startUpload();
  }

  function retryFailed(): void {
    if (failedUuids.value.length === 0 || isUploadInFlight.value) {
      return;
    }

    clearAutoReset();

    trackManualPartialRetry(failedUuids.value.length, retryCount.value);
    autoRetryAttempt.value = 0;
    void startUpload([...failedUuids.value]);
  }

  function finishWithPartial(): void {
    trackPartialAccepted({
      successCount: successCount.value,
      failedCount: failedUuids.value.length,
      totalCount: totalCount.value,
    });

    isPartialSuccess.value = false;
    isSuccess.value = true;
    failedUuids.value = [];
    scheduleReset(autoResetDelaySync.value);
  }

  async function resetKiosk(): Promise<void> {
    if (!isMounted.value) {
      return;
    }

    trackKioskReset(successCount.value, retryCount.value);
    const loadingIndicator = useUiStore().createLoadingIndicator();
    try {
      try {
        await session.end('completed');
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: preserve operational reset diagnostics.
        console.warn('[GoPhotoUpload] Session end failed:', error);
      }
      if (isMounted.value) {
        await router.redirectToStep('', {
          localeCode: currentLocale.value?.code,
        });
      }
    } finally {
      loadingIndicator.hide();
    }
  }

  const publicApi: GoPhotoUploadPublicApi = proxyRefs({
    AUTO_RESET_DELAY_BACKGROUND: autoResetDelayBackground,
    AUTO_RESET_DELAY_SYNC: autoResetDelaySync,
    AUTO_RETRY_DELAYS: autoRetryDelays,
    MAX_AUTO_RETRIES: maxAutoRetries,
    USE_BACKGROUND_UPLOAD: useBackgroundUpload,
    _doStartUpload: doStartUpload,
    _isMounted: isMounted,
    _isUploadInFlight: isUploadInFlight,
    _retryDelayWithJitter: configuredRetryDelay,
    get autoResetTimeout(): ReturnType<typeof setTimeout> | null {
      return autoResetTimeout;
    },
    set autoResetTimeout(value: ReturnType<typeof setTimeout> | null) {
      autoResetTimeout = value;
    },
    autoRetryAttempt,
    errorMessage,
    failedUuids,
    finishWithPartial,
    get gophotoCode(): string {
      return gophotoCode.value;
    },
    hasError,
    get hohPortraitsStillLoading(): boolean {
      return hohPortraitsStillLoading.value;
    },
    get hohSendsAllPortraits(): boolean {
      return hohSendsAllPortraits.value;
    },
    get idleWatcherPause(): IdleWatcherPause | null {
      return idleWatcherPause;
    },
    set idleWatcherPause(value: IdleWatcherPause | null) {
      idleWatcherPause = value;
    },
    isPartialSuccess,
    isSuccess,
    isUploading,
    resetKiosk,
    retry,
    retryCount,
    retryFailed,
    get selectedPortraits(): GoPhotoUploadPortrait[] {
      return selectedPortraits.value;
    },
    get showGophotoCode(): boolean {
      return showGophotoCode.value;
    },
    startUpload,
    store,
    successCount,
    totalCount,
    uploadedCount,
    uploadStartTime,
    get viewState(): GoPhotoUploadViewState {
      return viewState.value;
    },
  });

  onMounted(() => {
    isMounted.value = true;
    exposeToGlobalScope(publicApi, 'GoPhotoUpload');
    idleWatcherPause = idleWatcher.pause();

    if (hohSendsAllPortraits.value && hohPortraitsStillLoading.value) {
      stopHohWatch = watch(hohPortraitsStillLoading, (stillLoading) => {
        if (!stillLoading && isMounted.value) {
          stopHohWatch?.();
          stopHohWatch = null;
          void startUpload();
        }
      });
      return;
    }

    void startUpload();
  });

  onBeforeUnmount(() => {
    isMounted.value = false;
    stopHohWatch?.();
    stopHohWatch = null;

    idleWatcherPause?.continue();
    idleWatcherPause = null;

    clearAutoReset();
  });

  return {
    errorMessage,
    failedUuids,
    finishWithPartial,
    gophotoCode,
    publicApi,
    retry,
    retryFailed,
    showGophotoCode,
    successCount,
    totalCount,
    uploadedCount,
    viewState,
  };
}
